package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.network.api.TreeCorrectionApi
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.shared.data.mapper.toDomain
import fr.frux.shared.data.mapper.toDto
import fr.frux.shared.domain.model.AppliedCorrection
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.TreeCorrection
import fr.frux.shared.domain.model.TreeRevision
import fr.frux.shared.domain.model.TreeRevisionLog
import fr.frux.shared.domain.repository.TreeCorrectionRepository
import fr.frux.shared.dto.TreeCorrectionRequest
import fr.frux.shared.dto.TreeRevisionDto
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow

@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class TreeCorrectionRepositoryImpl(
    private val api: TreeCorrectionApi,
) : TreeCorrectionRepository {
    private val _applied = MutableSharedFlow<AppliedCorrection>(extraBufferCapacity = 8)
    override val applied: SharedFlow<AppliedCorrection> = _applied.asSharedFlow()

    override suspend fun correct(
        treeId: Long,
        correction: TreeCorrection,
        undo: TreeCorrection?,
        idempotencyKey: String,
    ): Result<Tree> =
        runCatchingCancellable {
            api.correctTree(treeId, correction.toRequest(), idempotencyKey).toDomain()
        }.onSuccess { tree ->
            _applied.tryEmit(AppliedCorrection(treeId = treeId, tree = tree, undo = undo))
        }.onFailure { Logger.e(TAG, "Échec de la correction de l'arbre $treeId", it) }
            .mapFailureToAppError()

    override suspend fun revisions(treeId: Long): Result<TreeRevisionLog> =
        runCatchingCancellable {
            val response = api.getTreeRevisions(treeId)
            TreeRevisionLog(revisions = response.revisions.map { it.toDomain() }, truncated = response.truncated)
        }.onFailure { Logger.e(TAG, "Échec de la lecture de l'historique de l'arbre $treeId", it) }
            .mapFailureToAppError()

    private companion object {
        const val TAG = "TreeCorrectionRepository"
    }
}

private fun TreeCorrection.toRequest(): TreeCorrectionRequest =
    when (this) {
        is TreeCorrection.Species -> TreeCorrectionRequest.Species(speciesId, certainty.toDto(), reason)
        is TreeCorrection.Position -> TreeCorrectionRequest.Position(latitude, longitude, reason)
        is TreeCorrection.Note -> TreeCorrectionRequest.Note(description, reason)
    }

// Lu tel quel : `field` est nul sur la ligne de création, ne jamais le déduire de `after`.
private fun TreeRevisionDto.toDomain(): TreeRevision =
    TreeRevision(
        kind = kind,
        field = field,
        before = before,
        after = after,
        authorName = authorName,
        reason = reason,
        source = source,
        redacted = redacted,
        atMillis = createdAt.toEpochMilliseconds(),
    )
